import type React from "react";
import styled, { css } from "styled-components";
import type { IconName } from "../icons";
import { Icon } from "../icons";
import ButtonWrapper from "./ButtonWrapper";
import type { ButtonProps as AntButtonProps } from "antd/es/button";
import buttonStyles from "./buttonStyles";
import {
  ghostPrimaryStyle,
  ghostStrokeStyle,
  ghostFilledStyle,
  ghostTextStyle,
  ghostLinkStyle,
  basicPrimaryStyle,
  basicStrokeStyle,
  basicFilledStyle,
  basicTextStyle,
  basicLinkStyle,
} from "./buttonTypes";
import {
  defaultSizeStyle,
  largeSizeStyle,
  smallSizeStyle,
} from "./buttonSizes";
import { deleteActionStyle, dropdownActionStyle } from "./buttonActions";

export interface ButtonProps extends Omit<AntButtonProps, "size" | "variant"> {
  children?: React.ReactNode;
  iconNameLeft?: IconName;
  iconNameRight?: IconName;
  buttonType?: ButtonType;
  buttonVariant?: ButtonVariant;
  buttonSize?: ButtonSize;
  buttonAction?: ButtonAction;
  noIconHover?: boolean;
}

export type ButtonVariant = "basic" | "ghost";
export type ButtonAction = "default" | "delete" | "dropdown";
export type ButtonType = "primary" | "stroke" | "filled" | "text" | "link";
export type ButtonSize = "default" | "tiny" | "small" | "large";

type StyledButtonProps = {
  $buttonType: ButtonType;
  $buttonVariant: ButtonVariant;
  $buttonSize: ButtonSize;
  $buttonAction: ButtonAction;
} & Omit<ButtonProps, "variant" | "colorScheme" | "buttonType">;

const Button = ({
  children,
  iconNameLeft,
  iconNameRight,
  buttonType = "primary",
  buttonVariant = "basic",
  buttonSize = "default",
  buttonAction = "default",
  noIconHover = false,
  ...rest
}: ButtonProps) => {
  return (
    <StyledButton
      $buttonType={buttonType}
      $buttonSize={buttonSize}
      $buttonVariant={buttonVariant}
      $buttonAction={buttonAction}
      {...rest}
    >
      {iconNameLeft && (
        <Icon iconName={iconNameLeft} noHover={noIconHover} noSize={true} />
      )}
      {children}
      {iconNameRight && (
        <Icon iconName={iconNameRight} noHover={noIconHover} noSize={true} />
      )}
    </StyledButton>
  );
};

export const buttonTypeStyles = (
  $buttonType: ButtonType,
  $buttonVariant: ButtonVariant,
) => {
  if ($buttonVariant === "ghost") {
    switch ($buttonType) {
      case "primary":
        return ghostPrimaryStyle;
      case "stroke":
        return ghostStrokeStyle;
      case "filled":
        return ghostFilledStyle;
      case "text":
        return ghostTextStyle;
      case "link":
        return ghostLinkStyle;
    }
  }

  if ($buttonVariant === "basic") {
    switch ($buttonType) {
      case "primary":
        return basicPrimaryStyle;
      case "stroke":
        return basicStrokeStyle;
      case "filled":
        return basicFilledStyle;
      case "text":
        return basicTextStyle;
      case "link":
        return basicLinkStyle;
    }
  }

  return css``;
};

export const sizeStyles = {
  default: defaultSizeStyle,
  large: largeSizeStyle,
  small: smallSizeStyle,
  tiny: smallSizeStyle,
};

export const actionStyles = {
  default: css``,
  delete: deleteActionStyle,
  dropdown: dropdownActionStyle,
};

const StyledButton = styled(ButtonWrapper)<StyledButtonProps>`
  ${buttonStyles}
  ${({ $buttonSize }) => sizeStyles[$buttonSize]}
  ${({ $buttonType, $buttonVariant }) =>
    buttonTypeStyles($buttonType, $buttonVariant)}
  ${({ $buttonAction }) => actionStyles[$buttonAction]}
`;

export default Button;
